export function formatGlCodeLabel(glCode, description) {
  const code = glCode != null ? String(glCode).trim() : "";
  const name = description != null ? String(description).trim() : "";
  if (!code) return "";
  return name ? `${code} - ${name}` : code;
}

export function newGlAllocation(glCode = "", share = "100") {
  return {
    client_id: `gl-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    gl_code: glCode,
    share: String(share),
  };
}

export function parseSharePercent(value) {
  if (value == null || String(value).trim() === "") return null;
  const n = parseFloat(String(value).replace(/%/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

export function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function allocateGlAmounts(totalAmount, shares) {
  const total = Number(totalAmount);
  const nums = (shares || []).map((share) => {
    const n = Number(share);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  if (!Number.isFinite(total)) return nums.map(() => null);
  const amounts = nums.map((share) => roundMoney((total * share) / 100));
  if (!amounts.length) return amounts;
  const shareSum = nums.reduce((sum, share) => sum + share, 0);
  if (Math.abs(shareSum - 100) > 0.005) return amounts;
  let lastIdx = amounts.length - 1;
  for (let i = amounts.length - 1; i >= 0; i -= 1) {
    if (nums[i] > 0) {
      lastIdx = i;
      break;
    }
  }
  const others = amounts.reduce(
    (sum, amount, index) => (index === lastIdx ? sum : sum + (amount || 0)),
    0,
  );
  amounts[lastIdx] = roundMoney(total - others);
  return amounts;
}

export function glShareTotal(allocations) {
  return (allocations || []).reduce(
    (sum, row) => sum + (parseSharePercent(row.share) || 0),
    0,
  );
}

export function areGlSharesComplete(allocations) {
  return Math.abs(glShareTotal(allocations) - 100) < 0.005;
}

export function formatGlAllocationsLabel(allocations, fallback = "") {
  const codes = (allocations || [])
    .map((row) => String(row?.gl_code || "").trim())
    .filter(Boolean);
  if (codes.length) return [...new Set(codes)].join(" / ");
  return fallback != null ? String(fallback).trim() : "";
}

export function invoiceHasGlCode(invoice) {
  return Boolean(formatGlAllocationsLabel(invoice?.gl_allocations, invoice?.gl_code || ""));
}

export function invoiceGlCodeValues(invoice) {
  const codes = (invoice?.gl_allocations || [])
    .map((row) => String(row?.gl_code || "").trim())
    .filter(Boolean);
  if (codes.length) return [...new Set(codes)];
  const fallback = invoice?.gl_code != null ? String(invoice.gl_code).trim() : "";
  return fallback ? [fallback] : [];
}

export function invoiceMatchesGlCode(invoice, glCode) {
  const selected = glCode != null ? String(glCode).trim().toLowerCase() : "";
  if (!selected) return true;
  return invoiceGlCodeValues(invoice).some((code) => code.toLowerCase() === selected);
}

export function allocationsFromInvoice(invoice) {
  const rows = Array.isArray(invoice?.gl_allocations) ? invoice.gl_allocations : [];
  if (rows.length) {
    return rows.map((row) =>
      newGlAllocation(
        row.gl_code || "",
        row.share_percent != null ? String(row.share_percent) : "100",
      ),
    );
  }
  return [newGlAllocation(invoice?.gl_code || "", "100")];
}

export function serializeGlAllocations(allocations) {
  return (allocations || []).map((row) => ({
    gl_code: String(row.gl_code || "").trim(),
    share_percent: parseSharePercent(row.share),
  }));
}

export function areGlAllocationsValid(allocations) {
  const rows = allocations || [];
  if (!rows.length) return false;
  const seen = new Set();
  for (const row of rows) {
    const code = String(row.gl_code || "").trim();
    const share = parseSharePercent(row.share);
    if (!code || share == null || share <= 0) return false;
    const key = code.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
  }
  return areGlSharesComplete(rows);
}

export function glCodesForDropdown(glCodes, currentValue = "") {
  const list = Array.isArray(glCodes) ? glCodes : [];
  const active = list.filter((item) => item && item.is_active !== false);
  const current = currentValue != null ? String(currentValue).trim() : "";
  if (!current) return active;

  const alreadyIncluded = active.some(
    (item) => String(item.gl_code || "").trim().toLowerCase() === current.toLowerCase(),
  );
  if (alreadyIncluded) return active;

  const existing = list.find(
    (item) => String(item.gl_code || "").trim().toLowerCase() === current.toLowerCase(),
  );
  if (existing) return [...active, existing];
  return [...active, { gl_code: current, description: "", is_active: false }];
}
